import clsx from 'clsx';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../../utils/dateUtils';
import { convertToFirstLetterCapital, isEmpty } from '../../../../../../utils/utils';
import { caseCategoryLabel, caseCategoryTone, CaseCategoryTone } from '../caseCategory';
import { STATUS } from '../legalCasesConstants';
import styles from './CaseDetailsDrawer.module.scss';
import CaseDocumentLinks from './CaseDocumentLinks';
import {
    DOWNLOAD_ALL_LABEL,
    OUTCOME_IN_FAVOUR_PREFIX,
    PARTY_NOUN,
    PARTY_ROW_SUFFIX,
    ROW_LABELS,
    SECTION_LABEL,
    SECTION_TITLES,
} from './constants';
import {
    ActAndSection,
    CaseDetailsDrawerProps,
    CaseOutcome,
    CaseSeverity,
    CaseStatus,
    DetailRow,
    DetailSectionData,
    FirDetail,
    LegalCaseDetails,
    PartyDetail,
} from './types';

type DocumentHandlers = Pick<CaseDetailsDrawerProps, 'onDocumentClick' | 'onDownloadAllClick'>;

const joinList = (values?: string[]) => (values ?? []).filter((value) => !isEmpty(value)).join(', ');

const joinLines = (values: string[]) => values.filter(Boolean).join('\n');

const formatDate = (date?: number | string) => formatDateByFormat(date, DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY);

const formatAct = ({ underActs, underSections }: ActAndSection) => {
    const sections = joinList(underSections);
    return [underActs, sections && `${SECTION_LABEL} ${sections}`].filter(Boolean).join(' - ');
};

const formatFir = ({ policeStation, firNo, firYear }: FirDetail) => [policeStation, firNo, firYear].filter(Boolean).join(', ');

const chip = (label: string, variantClass?: string) => <span className={clsx(styles.Chip, variantClass)}>{label}</span>;

const SEVERITY_CHIP_CLASS: Record<CaseSeverity, string> = {
    high: styles.Chip_HighRisk,
    medium: styles.Chip_MediumRisk,
    low: styles.Chip_Positive,
};

const STATUS_CHIP_CLASS: Record<string, string> = {
    [STATUS.PENDING.value]: styles.Chip_Pending,
    [STATUS.DISPOSED.value]: styles.Chip_Disposed,
};

export const statusChip = (caseStatus?: CaseStatus) => (caseStatus ? chip(caseStatus, STATUS_CHIP_CLASS[caseStatus]) : null);

const CATEGORY_TONE_CLASS: Record<CaseCategoryTone, string> = {
    civil: styles.CaseCategory_Civil,
    criminal: styles.CaseCategory_Criminal,
    neutral: '',
};

const caseTypeValue = ({ caseCategory, standardCaseType }: LegalCaseDetails) => {
    const label = caseCategoryLabel(caseCategory);
    if (!label) return standardCaseType ? <span className={styles.StandardCaseType}>{standardCaseType}</span> : null;
    return (
        <>
            <span className={clsx(styles.CaseCategory, CATEGORY_TONE_CLASS[caseCategoryTone(caseCategory)])}>{label}</span>
            {standardCaseType && <span className={styles.StandardCaseType}>({standardCaseType})</span>}
        </>
    );
};

const outcomeChip = (caseOutcome?: CaseOutcome | null) => {
    const inFavour = caseOutcome?.inFavour;
    if (!inFavour) return null;
    return chip(`${OUTCOME_IN_FAVOUR_PREFIX} ${convertToFirstLetterCapital(inFavour)}`, styles.Chip_Positive);
};

const documentsRow = (caseDetails: LegalCaseDetails, { onDocumentClick, onDownloadAllClick }: DocumentHandlers): DetailRow => {
    const documents = caseDetails.caseDocuments ?? [];
    if (!documents.length) return { label: ROW_LABELS.CASE_DOCUMENTS, value: null };

    return {
        label: ROW_LABELS.CASE_DOCUMENTS,
        labelAction: (
            <button type="button" className={styles.DownloadAll} onClick={onDownloadAllClick}>
                {DOWNLOAD_ALL_LABEL}
            </button>
        ),
        value: <CaseDocumentLinks documents={documents} onDocumentClick={onDocumentClick} />,
    };
};

const partyRows = (parties: PartyDetail[] | undefined, noun: string): DetailRow[] => {
    if (!parties?.length) return [{ label: `${noun} 1`, value: null }];

    return parties.flatMap((party, index) => {
        const position = `${noun} ${index + 1}`;
        return [
            { label: position, value: party.name, startsGroup: index > 0 },
            { label: `${position} ${PARTY_ROW_SUFFIX.ADDRESS}`, value: party.address },
            { label: `${position} ${PARTY_ROW_SUFFIX.ADVOCATE_NAME}`, value: joinList(party.advocates) },
        ];
    });
};

export const buildCaseDetailsSections = (caseDetails: LegalCaseDetails, handlers: DocumentHandlers): DetailSectionData[] => [
    {
        title: SECTION_TITLES.SUMMARY,
        defaultExpanded: true,
        rows: [
            { label: ROW_LABELS.CASE_TYPE, value: caseTypeValue(caseDetails) },
            { label: ROW_LABELS.CASE_SUMMARY, value: caseDetails.caseSummary },
            { label: ROW_LABELS.PETITIONERS, value: joinList(caseDetails.petitioners) },
            { label: ROW_LABELS.RESPONDENTS, value: joinList(caseDetails.respondents) },
            { label: ROW_LABELS.ADVOCATE_NAME, value: joinList(caseDetails.advocateNames) },
        ],
    },
    {
        title: SECTION_TITLES.STATUS,
        defaultExpanded: true,
        rows: [
            { label: ROW_LABELS.STATUS, value: statusChip(caseDetails.caseStatus) },
            { label: ROW_LABELS.FILING_DATE, value: formatDate(caseDetails.filingDate) },
            { label: ROW_LABELS.NEXT_HEARING_DATE, value: formatDate(caseDetails.nextHearingDate) },
            { label: ROW_LABELS.DECISION_DATE, value: formatDate(caseDetails.decisionDate) },
            { label: ROW_LABELS.CASE_OUTCOME, value: outcomeChip(caseDetails.caseOutcome) },
            { label: ROW_LABELS.NATURE_OF_DISPOSAL, value: caseDetails.natureOfDisposal },
            { label: ROW_LABELS.STAGE_OF_CASE, value: caseDetails.stageOfCase },
        ],
    },
    {
        title: SECTION_TITLES.DETAILS,
        defaultExpanded: true,
        rows: [
            documentsRow(caseDetails, handlers),
            { label: ROW_LABELS.CASE_NUMBER, value: caseDetails.casetypeCaseNoCaseYr },
            ...(caseDetails.severity
                ? [
                      {
                          label: ROW_LABELS.SEVERITY,
                          value: chip(convertToFirstLetterCapital(caseDetails.severity), SEVERITY_CHIP_CLASS[caseDetails.severity]),
                      },
                  ]
                : []),
            { label: ROW_LABELS.CINO, value: caseDetails.cino },
            { label: ROW_LABELS.CIVIL_CRIMINAL, value: caseDetails.civilCriminal },
            { label: ROW_LABELS.CASE_THEME, value: joinList(caseDetails.caseThemes) },
            { label: ROW_LABELS.ACTS_AND_SECTIONS, value: joinLines((caseDetails.actsAndSections ?? []).map(formatAct)) },
            { label: ROW_LABELS.FIR_DETAILS, value: joinLines((caseDetails.firDetails ?? []).map(formatFir)) },
        ],
    },
    {
        title: SECTION_TITLES.COURT,
        defaultExpanded: false,
        rows: [
            { label: ROW_LABELS.COURT, value: caseDetails.court },
            { label: ROW_LABELS.PRESIDING_JUDGE, value: joinList(caseDetails.presidingJudges) },
            { label: ROW_LABELS.COURT_ESTABLISHMENT, value: caseDetails.courtEstablishment },
            { label: ROW_LABELS.STATE, value: caseDetails.state },
            { label: ROW_LABELS.DISTRICT, value: caseDetails.district },
        ],
    },
    {
        title: SECTION_TITLES.PETITIONERS,
        defaultExpanded: false,
        rows: partyRows(caseDetails.petitionerDetails, PARTY_NOUN.PETITIONER),
    },
    {
        title: SECTION_TITLES.RESPONDENTS,
        defaultExpanded: false,
        rows: partyRows(caseDetails.respondentDetails, PARTY_NOUN.RESPONDENT),
    },
];
